/**
 * 加密分钟盘导航会话（实例级）。
 * 禁止再使用模块全局 silent/suppress/swap flag——见 crypto-updown.mdc。
 */

import {
  buildMinuteUpdownSlug,
  buildDailyUpdownSlugFromStart,
  isCryptoUpdownDetailSlug,
  parseMinuteUpdownSlug,
  resolveUpdownRoundSlugSource,
  reconcileIntervalUpdownSlugWindow,
  resolveUpdownSlugEmbedModeFromRow,
} from '@/domain/crypto/crypto-minute-slug.js';
import { buildHourlyEtUpdownSlugFromStart } from '@/domain/crypto/crypto-updown-et-time.js';
import { resolveCollectionSlug } from '@/domain/market/collection-slug.js';
import { getMarketCollectionById } from '@/api/market';
import { resolveUpdownRoundByTs } from '@/lib/crypto/fetchUpdownRoundByTs.js';
import {
  isUpdownRoundLive,
  parseUpdownIntervalSeconds,
  resolveLiveRoundStartSec,
  resolveViewingUpdownRound,
} from '@/domain/crypto/crypto-updown-series.js';
import { shouldAutoNavigateUpdownToLive } from '@/domain/crypto/crypto-updown-navigation-policy.js';

export const CRYPTO_UPDOWN_SERIES_QUERY_KEY = 'sk';
/** 页内点历史档：保留该档，外链 beforeEnter 不纠偏到 live */
export const CRYPTO_UPDOWN_HIST_QUERY_KEY = 'hist';

/** 禁止写入详情 URL 的噪音参数（标题/乐观 UI/系列键均由 API 或内存提供） */
const UPDOWN_URL_NOISE_KEYS = ['t', 'sk', 'v', 'y', 'n', 'ps'];

/** 同系列切盘 keepAlive：记在内存，不依赖 URL ?sk= */
let updownKeepAliveSeriesKey = '';
/** @type {Map<string, string>} */
const updownSeriesKeyByCollectionId = new Map();
/** @type {Map<string, string>} 进页时带上的完整 slug，供 beforeEnter 免 get 判断是否 live */
const updownSlugByCollectionId = new Map();

export function rememberCryptoUpdownKeepAliveSeriesKey(seriesKey, collectionId = '') {
  const sk = String(seriesKey ?? '').trim();
  if (!sk) return;
  updownKeepAliveSeriesKey = sk;
  const id = String(collectionId ?? '').trim();
  if (id) updownSeriesKeyByCollectionId.set(id, sk);
}

export function rememberCryptoUpdownRoundSlug(collectionId, slug = '') {
  const id = String(collectionId ?? '').trim();
  const value = String(slug ?? '').trim();
  if (!id || !value) return;
  updownSlugByCollectionId.set(id, value);
}

export function peekCryptoUpdownKeepAliveSeriesKey(collectionId = '') {
  const id = String(collectionId ?? '').trim();
  if (id) {
    const byId = updownSeriesKeyByCollectionId.get(id);
    if (byId) return byId;
  }
  return updownKeepAliveSeriesKey;
}

export function peekCryptoUpdownRoundSlug(collectionId = '') {
  const id = String(collectionId ?? '').trim();
  if (!id) return '';
  return updownSlugByCollectionId.get(id) || '';
}

export function clearCryptoUpdownKeepAliveSeriesKey() {
  updownKeepAliveSeriesKey = '';
  updownSeriesKeyByCollectionId.clear();
  updownSlugByCollectionId.clear();
}

function stripUpdownNoiseQuery(query) {
  if (!query || typeof query !== 'object') return {};
  const next = { ...query };
  for (const key of UPDOWN_URL_NOISE_KEYS) delete next[key];
  return next;
}

function updownQueryNeedsStrip(query) {
  if (!query || typeof query !== 'object') return false;
  return UPDOWN_URL_NOISE_KEYS.some((key) => Object.prototype.hasOwnProperty.call(query, key));
}

/** 从 seriesKey 解析周期标签：btc-updown-5m / btc-updown-4h / bitcoin-up-or-down / bitcoin-up-or-down-1h */
export function resolveUpdownIntervalLabelFromSeriesKey(seriesKey) {
  const key = String(seriesKey ?? '')
    .trim()
    .toLowerCase();
  if (!key) return '5m';
  if (/-up-or-down-1h$/i.test(key)) return '1h';
  if (/-up-or-down$/i.test(key)) return '1d';
  const match = key.match(/-updown-(\d+[mh])$/i);
  return match?.[1]?.toLowerCase() || '5m';
}

function resolveUpdownSlugFromCollection(data) {
  return resolveUpdownRoundSlugSource(data) || resolveCollectionSlug(data) || '';
}

/**
 * keepAlive 分桶：同 series 复用，换币种/周期销毁。
 * 优先内存 seriesKey；兼容旧链接 ?sk=；否则按 id 隔离。
 */
export function resolveCryptoUpdownKeepAliveKey(route) {
  if (String(route?.name ?? '') !== 'CryptoUpdownDetail') {
    return String(route?.name ?? '');
  }
  const fromQuery = String(route?.query?.[CRYPTO_UPDOWN_SERIES_QUERY_KEY] ?? '').trim();
  if (fromQuery) {
    rememberCryptoUpdownKeepAliveSeriesKey(fromQuery, route?.params?.id);
    return `CryptoUpdownDetail:${fromQuery}`;
  }
  const remembered = peekCryptoUpdownKeepAliveSeriesKey(route?.params?.id);
  if (remembered) return `CryptoUpdownDetail:${remembered}`;
  const id = String(route?.params?.id ?? '').trim();
  return id ? `CryptoUpdownDetail:id:${id}` : 'CryptoUpdownDetail';
}

/** 详情 query：仅历史档 hist=1；系列键走内存，不进 URL */
export function buildCryptoUpdownDetailQuery({ historical = false } = {}) {
  if (!historical) return undefined;
  return { [CRYPTO_UPDOWN_HIST_QUERY_KEY]: '1' };
}

/**
 * 统一分钟盘详情路由（全入口复用）。
 * seriesKey 写入 keepAlive 内存；URL 默认仅 `/crypto/updown/:id`（历史加 hist）。
 * @returns {{ name: string, params: { id: string }, query?: Record<string, string> } | null}
 */
export function buildCryptoUpdownDetailRoute({
  id = '',
  seriesKey = '',
  slug = '',
  historical = false,
  query: extraQuery = null,
} = {}) {
  const collectionId = String(id ?? '').trim();
  if (!collectionId) return null;

  const slugNorm = String(slug ?? '').trim();
  // PM classic daily / 目标价不进 Updown；PM 小时 ET 与 4h 一样进切盘壳。
  if (slugNorm && !isCryptoUpdownDetailSlug(slugNorm)) return null;

  const sk = String(seriesKey ?? '').trim() || parseMinuteUpdownSlug(slugNorm)?.seriesKey || '';
  if (sk) rememberCryptoUpdownKeepAliveSeriesKey(sk, collectionId);
  if (slugNorm) rememberCryptoUpdownRoundSlug(collectionId, slugNorm);

  const query = stripUpdownNoiseQuery({
    ...(buildCryptoUpdownDetailQuery({ historical }) || {}),
    ...(extraQuery && typeof extraQuery === 'object' ? extraQuery : {}),
  });

  return {
    name: 'CryptoUpdownDetail',
    params: { id: collectionId },
    query: Object.keys(query).length ? query : undefined,
  };
}

/**
 * 外链进详情：解析当前 clock live 的 collection，再拼路由。
 * 已是 live 则保留传入 id；解析失败回退原 id。
 * 列表进页请用同步 `buildCryptoUpdownDetailRoute`，勿在 push 前 await 本函数。
 */
export async function resolveCryptoUpdownLiveDetailRoute({
  id = '',
  seriesKey = '',
  slug = '',
  query: extraQuery = null,
} = {}) {
  const fallbackId = String(id ?? '').trim();
  const parsed = parseMinuteUpdownSlug(slug);
  const sk = String(seriesKey ?? '').trim() || parsed?.seriesKey || '';
  const interval = parseUpdownIntervalSeconds(parsed?.interval || '5m');
  const nowSec = Math.floor(Date.now() / 1000);

  if (fallbackId && parsed?.kind === 'interval' && Number(parsed.slugUnix) > 0) {
    // Only keep on start-embed live (PM). End-embed "live" of a *next* PM start-slug
    // equals the current window — that false-positive must fall through to
    // resolveUpdownRoundByTs (start-first probe). SELF list→detail relies on
    // ensureCryptoUpdownRouteIsLive + collection endDate/liquiditySource instead.
    const startTs = reconcileIntervalUpdownSlugWindow({
      slugUnix: parsed.slugUnix,
      intervalSec: interval,
      embedMode: 'start',
    }).roundTs;
    if (isUpdownRoundLive(startTs, nowSec, interval)) {
      return buildCryptoUpdownDetailRoute({
        id: fallbackId,
        seriesKey: sk,
        slug,
        query: extraQuery,
      });
    }
  } else if (fallbackId && parsed?.roundTs && isUpdownRoundLive(parsed.roundTs, nowSec, interval)) {
    return buildCryptoUpdownDetailRoute({
      id: fallbackId,
      seriesKey: sk,
      slug,
      query: extraQuery,
    });
  }

  if (!sk) {
    return buildCryptoUpdownDetailRoute({
      id: fallbackId,
      seriesKey: sk,
      slug,
      query: extraQuery,
    });
  }

  const liveTs = resolveLiveRoundStartSec(nowSec, interval);
  const live = await resolveUpdownRoundByTs({
    seriesKey: sk,
    roundTs: liveTs,
    intervalSec: interval,
    collectionId: fallbackId,
  });

  const liveId = String(live?.collectionId ?? '').trim();
  if (liveId) {
    const liveSlug =
      String(live?.slug ?? live?.eventSlug ?? '').trim() ||
      (parsed?.kind === 'hourly'
        ? buildHourlyEtUpdownSlugFromStart(parsed.fullAsset || parsed.seriesKey, liveTs)
        : parsed?.kind === 'daily'
        ? buildDailyUpdownSlugFromStart(parsed.seriesKey, liveTs)
        : parsed?.asset
        ? buildMinuteUpdownSlug(parsed.asset, parsed.interval || '5m', liveTs, 'start')
        : '');
    return buildCryptoUpdownDetailRoute({
      id: liveId,
      seriesKey: sk,
      slug: liveSlug,
      query: extraQuery,
    });
  }

  return buildCryptoUpdownDetailRoute({
    id: fallbackId,
    seriesKey: sk,
    slug,
    query: extraQuery,
  });
}

/** 从 collection 拉 slug → seriesKey（进页补 sk 用） */
export async function resolveCryptoUpdownSeriesKeyByCollectionId(collectionId) {
  const id = String(collectionId ?? '').trim();
  if (!id) return '';
  try {
    const raw = await getMarketCollectionById(id);
    const data = raw?.data ?? raw;
    const slug = resolveUpdownSlugFromCollection(data);
    return String(parseMinuteUpdownSlug(slug)?.seriesKey ?? '').trim();
  } catch {
    return '';
  }
}

/**
 * 一次 getCollection：同时拿到 sk + slug + collection（避免 beforeEnter 重复打详情）
 * @returns {{ sk: string, slug: string, collection?: object } | null}
 */
async function fetchUpdownRouteMetaByCollectionId(collectionId) {
  const id = String(collectionId ?? '').trim();
  if (!id) return null;
  try {
    const raw = await getMarketCollectionById(id);
    const data = raw?.data ?? raw;
    const slug = resolveUpdownSlugFromCollection(data);
    const sk = String(parseMinuteUpdownSlug(slug)?.seriesKey ?? '').trim();
    return { sk, slug: String(slug ?? '').trim(), collection: data };
  } catch {
    return null;
  }
}

/**
 * 进页前解析 seriesKey 写入 keepAlive 内存（不再写入 URL ?sk=）。
 * @returns {import('vue-router').RouteLocationRaw | null} 仅当需要剥噪音 query 时返回 replace
 */
export async function ensureCryptoUpdownRouteHasSk(to) {
  const id = to.params?.id;
  let sk =
    String(to.query?.[CRYPTO_UPDOWN_SERIES_QUERY_KEY] ?? '').trim() ||
    peekCryptoUpdownKeepAliveSeriesKey(id);
  if (!sk) {
    const meta = await fetchUpdownRouteMetaByCollectionId(id);
    sk = meta?.sk || '';
  }
  if (sk) rememberCryptoUpdownKeepAliveSeriesKey(sk, id);
  if (!updownQueryNeedsStrip(to.query)) return null;
  return {
    name: to.name,
    params: { ...to.params },
    query: stripUpdownNoiseQuery(to.query),
    hash: to.hash,
    replace: true,
  };
}

/**
 * 进页纠偏到 clock live（含 5m；query.hist=1 为历史档，跳过）。
 * 页内 5m 到期停留结算 → 裁决后再 auto-live，见 orchestration。
 * 有内存 slug 时可免 get 判断是否 live；非 live 才 cryptoPageBySlug 找 live id。
 * @returns {import('vue-router').RouteLocationRaw | null}
 */
export async function ensureCryptoUpdownRouteIsLive(to, prefetchedMeta = null) {
  if (String(to.query?.[CRYPTO_UPDOWN_HIST_QUERY_KEY] ?? '').trim()) return null;

  const routeId = String(to.params?.id ?? '').trim();
  let sk = String(to.query?.[CRYPTO_UPDOWN_SERIES_QUERY_KEY] ?? '').trim();
  let currentSlug = peekCryptoUpdownRoundSlug(routeId);
  let collection = prefetchedMeta?.collection ?? null;

  if (prefetchedMeta?.sk || prefetchedMeta?.slug) {
    sk = sk || String(prefetchedMeta.sk ?? '').trim();
    currentSlug = currentSlug || String(prefetchedMeta.slug ?? '').trim();
  }
  if (!sk) sk = peekCryptoUpdownKeepAliveSeriesKey(routeId);

  if (!sk || !currentSlug || !collection) {
    const meta = await fetchUpdownRouteMetaByCollectionId(routeId);
    if (meta) {
      sk = sk || meta.sk;
      currentSlug = currentSlug || meta.slug;
      collection = collection || meta.collection || null;
      if (meta.slug) rememberCryptoUpdownRoundSlug(routeId, meta.slug);
    }
  }
  if (!sk) return null;
  // Defensive: never live-correct PM daily / 目标价 into the Updown series.
  if (currentSlug && !isCryptoUpdownDetailSlug(currentSlug)) return null;
  rememberCryptoUpdownKeepAliveSeriesKey(sk, routeId);

  const intervalLabel = resolveUpdownIntervalLabelFromSeriesKey(sk);
  const interval = parseUpdownIntervalSeconds(intervalLabel);
  // 进页一律对齐 clock live（含 5m「下一档」误入）
  if (!shouldAutoNavigateUpdownToLive(interval, { onEnter: true })) return null;
  const nowSec = Math.floor(Date.now() / 1000);
  const liveTs = resolveLiveRoundStartSec(nowSec, interval);

  // Prefer collection endDate / liquiditySource over bare slug parse (PM start-embed).
  let currentTs = 0;
  if (collection) {
    currentTs = Number(resolveViewingUpdownRound(collection, currentSlug, interval)?.roundTs) || 0;
  }
  if (!currentTs && currentSlug) {
    const market = Array.isArray(collection?.markets) ? collection.markets[0] : null;
    const parsed = parseMinuteUpdownSlug(currentSlug);
    if (parsed?.kind === 'interval' && Number(parsed.slugUnix) > 0) {
      currentTs =
        reconcileIntervalUpdownSlugWindow({
          slugUnix: parsed.slugUnix,
          intervalSec: interval,
          embedMode: resolveUpdownSlugEmbedModeFromRow(collection, market) || parsed.slugEmbed,
        }).roundTs || 0;
    } else {
      currentTs = parsed?.roundTs || 0;
    }
  }
  if (currentTs && isUpdownRoundLive(currentTs, nowSec, interval)) return null;

  const live = await resolveUpdownRoundByTs({
    seriesKey: sk,
    roundTs: liveTs,
    intervalSec: interval,
    collectionId: routeId,
    collection,
  });
  const liveId = String(live?.collectionId ?? '').trim();
  if (!liveId || liveId === routeId) return null;
  rememberCryptoUpdownKeepAliveSeriesKey(sk, liveId);
  const liveSlug = String(live?.slug ?? live?.eventSlug ?? '').trim();
  if (liveSlug) rememberCryptoUpdownRoundSlug(liveId, liveSlug);

  const nextQuery = stripUpdownNoiseQuery(to.query);
  delete nextQuery[CRYPTO_UPDOWN_HIST_QUERY_KEY];

  return {
    name: to.name,
    params: { ...to.params, id: liveId },
    query: Object.keys(nextQuery).length ? nextQuery : undefined,
    hash: to.hash,
    replace: true,
  };
}

/**
 * beforeEnter：列表同步 push 不 await；进页守卫负责非 hist 纠到 clock live（含 5m）。
 * 历史仓位等带 ?hist=1 不纠；页内 5m 到期停留结算，裁决后由 orchestration auto-live。
 * @returns {import('vue-router').RouteLocationRaw | true}
 */
export async function ensureCryptoUpdownDetailEnter(to) {
  const routeId = String(to.params?.id ?? '').trim();
  const hasHist = Boolean(String(to.query?.[CRYPTO_UPDOWN_HIST_QUERY_KEY] ?? '').trim());
  const needsStrip = updownQueryNeedsStrip(to.query);

  let sk =
    String(to.query?.[CRYPTO_UPDOWN_SERIES_QUERY_KEY] ?? '').trim() ||
    peekCryptoUpdownKeepAliveSeriesKey(routeId);
  let slug = peekCryptoUpdownRoundSlug(routeId);
  let meta = null;

  if (sk) rememberCryptoUpdownKeepAliveSeriesKey(sk, routeId);

  const cleanedQuery = stripUpdownNoiseQuery(to.query);
  if (hasHist) cleanedQuery[CRYPTO_UPDOWN_HIST_QUERY_KEY] = '1';

  const redirectIfNotUpdownDetail = (candidateSlug) => {
    const s = String(candidateSlug ?? '').trim();
    if (!s) return null;
    if (isCryptoUpdownDetailSlug(s)) return null;
    return {
      name: 'MarketDetail',
      params: { id: routeId },
      replace: true,
    };
  };

  if (hasHist) {
    if (!sk) {
      meta = await fetchUpdownRouteMetaByCollectionId(routeId);
      sk = meta?.sk || '';
      slug = slug || meta?.slug || '';
      if (sk) rememberCryptoUpdownKeepAliveSeriesKey(sk, routeId);
      if (meta?.slug) rememberCryptoUpdownRoundSlug(routeId, meta.slug);
    }
    const bounced = redirectIfNotUpdownDetail(slug);
    if (bounced) return bounced;
    if (!needsStrip) return true;
    return {
      name: to.name,
      params: { ...to.params },
      query: Object.keys(cleanedQuery).length ? cleanedQuery : undefined,
      hash: to.hash,
      replace: true,
    };
  }

  // 无内存 slug/sk 时补一次 get；已有则尽量零 get 判断 live
  if (!sk || !slug) {
    meta = await fetchUpdownRouteMetaByCollectionId(routeId);
    sk = sk || meta?.sk || '';
    slug = slug || meta?.slug || '';
    if (sk) rememberCryptoUpdownKeepAliveSeriesKey(sk, routeId);
    if (slug) rememberCryptoUpdownRoundSlug(routeId, slug);
  }

  const bounced = redirectIfNotUpdownDetail(slug);
  if (bounced) return bounced;

  const cleanedTo = {
    ...to,
    query: cleanedQuery,
  };

  const withLive = await ensureCryptoUpdownRouteIsLive(cleanedTo, {
    sk,
    slug,
    collection: meta?.collection ?? null,
  });
  if (withLive) return withLive;

  if (needsStrip) {
    return {
      name: to.name,
      params: { ...to.params },
      query: Object.keys(cleanedQuery).length ? cleanedQuery : undefined,
      hash: to.hash,
      replace: true,
    };
  }
  return true;
}

export function createCryptoUpdownNavSession() {
  let silentNextDetailLoad = false;
  let suppressRouteDetailLoadCount = 0;
  let roundSwapInProgress = false;
  /** 同一次进入（series+route）自动 go-live 只尝试一次 */
  let autoLiveAttemptKey = '';
  let pageActive = true;

  return {
    markSilentDetailLoad() {
      silentNextDetailLoad = true;
    },
    peekSilentDetailLoad() {
      return silentNextDetailLoad;
    },
    consumeSilentDetailLoad() {
      const silent = silentNextDetailLoad;
      silentNextDetailLoad = false;
      return silent;
    },
    scheduleClearSilentDetailLoad() {
      queueMicrotask(() => {
        silentNextDetailLoad = false;
      });
    },
    suppressRouteDetailLoad() {
      suppressRouteDetailLoadCount += 1;
    },
    consumeRouteDetailLoadSuppressed() {
      if (suppressRouteDetailLoadCount <= 0) return false;
      suppressRouteDetailLoadCount -= 1;
      return true;
    },
    beginRoundSwap() {
      roundSwapInProgress = true;
    },
    endRoundSwap() {
      roundSwapInProgress = false;
      // swap 自管 load；replace 时 watch 常被 isRoundSwapInProgress 短路未消费 suppress
      suppressRouteDetailLoadCount = 0;
    },
    isRoundSwapInProgress() {
      return roundSwapInProgress;
    },
    setPageActive(active) {
      pageActive = Boolean(active);
    },
    isPageActive() {
      return pageActive;
    },
    /** @returns {boolean} true = 首次，已记账；false = 已尝试过 */
    tryBeginAutoLive(attemptKey) {
      const key = String(attemptKey ?? '').trim();
      if (!key) return false;
      if (autoLiveAttemptKey === key) return false;
      autoLiveAttemptKey = key;
      return true;
    },
    clearAutoLiveAttempt() {
      autoLiveAttemptKey = '';
    },
    reset() {
      silentNextDetailLoad = false;
      suppressRouteDetailLoadCount = 0;
      roundSwapInProgress = false;
      autoLiveAttemptKey = '';
      pageActive = false;
    },
  };
}
